import type { ElementType } from "react";
import { Localizer } from "@/main/react/Types";
import { ArrowUp, CalendarDays, Flame, MessageCircle, MessageSquareText, ArrowBigDown, ArrowBigUp, UserPlus2, Users } from "lucide-react";

export type ProfilePageSelectOption = {
    key: string;
    value: string;
    label: string;
    icon?: ElementType;
};

export type UserDiscussionOrderingStyle = "new" | "active" | "top" | "participated";
export type UserThoughtOrderingStyle = "new" | "votes" | "agreements" | "disagreements" | "reacted";
export type UserGroupOrderingStyle = "joined" | "new" | "members" | "discussions" | "active";
export type UserSeriesOrderingStyle = "new" | "followers" | "discussions";
type UserGroupOrderingPerspective = "dashboard" | "publicProfile";

export type UserDiscussionOrderingMode = "authored" | "participated" | "deleted";

export function normalizeUserDiscussionOrdering(value?: string, mode: UserDiscussionOrderingMode = "authored"): UserDiscussionOrderingStyle {
    // EMPTY-STATES-77 (D34): the deleted view has exactly one ordering — by the deletion date its
    // cards stamp. Every value, including a stale ?ordering=top URL, normalizes to "new"; the
    // repository maps the deleted mode's "new" onto deleted_at DESC (newest-deleted first).
    if (mode === "deleted") {
        return "new";
    }
    switch ((value ?? "").trim().toLowerCase()) {
        case "active":
            return "active";
        case "top":
            return "top";
        case "participated":
            return "participated";
        default:
            return mode === "authored" ? "new" : "participated";
    }
}

export function getUserDiscussionOrderingOptions(localizer: Localizer, mode: UserDiscussionOrderingMode = "authored"): ProfilePageSelectOption[] {
    // EMPTY-STATES-77 (D34): Active and Top are properties of a post's live life, not of its
    // deletion, so the deleted view offers only "Newest" — coherent with the "Deleted {0}" stamps
    // now that the list orders on deleted_at.
    if (mode === "deleted") {
        return [
            { key: "new", value: "new", label: localizer("profile.discussions.ordering.new"), icon: CalendarDays },
        ];
    }

    const base: ProfilePageSelectOption[] = [
        { key: "new", value: "new", label: localizer("profile.discussions.ordering.new"), icon: CalendarDays },
        { key: "active", value: "active", label: localizer("profile.discussions.ordering.active"), icon: Flame },
        { key: "top", value: "top", label: localizer("profile.discussions.ordering.top"), icon: ArrowUp },
    ];

    return mode === "authored"
        ? base
        : [{ key: "participated", value: "participated", label: localizer("profile.discussions.ordering.participated"), icon: MessageCircle }, ...base];
}

export function normalizeUserArgumentOrdering(value?: string, mode: "authored" | "reacted" = "authored"): UserThoughtOrderingStyle {
    switch ((value ?? "").trim().toLowerCase()) {
        case "votes":
            return "votes";
        case "agreements":
            return "agreements";
        case "disagreements":
            return "disagreements";
        case "reacted":
            return "reacted";
        default:
            return mode === "authored" ? "new" : "reacted";
    }
}

export function getUserArgumentOrderingOptions(localizer: Localizer, mode: "authored" | "reacted" = "authored"): ProfilePageSelectOption[] {
    const base: ProfilePageSelectOption[] = [
        { key: "new", value: "new", label: localizer("profile.thoughts.ordering.new"), icon: CalendarDays },
        { key: "votes", value: "votes", label: localizer("profile.thoughts.ordering.votes"), icon: Users },
        { key: "agreements", value: "agreements", label: localizer("profile.thoughts.ordering.upvotes"), icon: ArrowBigUp },
        { key: "disagreements", value: "disagreements", label: localizer("profile.thoughts.ordering.downvotes"), icon: ArrowBigDown },
    ];

    return mode === "authored"
        ? base
        : [{ key: "reacted", value: "reacted", label: localizer("profile.thoughts.ordering.reacted"), icon: MessageCircle }, ...base];
}

export function normalizeUserGroupOrdering(value?: string): UserGroupOrderingStyle {
    switch ((value ?? "").trim().toLowerCase()) {
        case "new":
            return "new";
        case "members":
            return "members";
        case "discussions":
            return "discussions";
        case "active":
            return "active";
        default:
            return "joined";
    }
}

export function getUserGroupOrderingOptions(
    localizer: Localizer,
    scope: "leader" | "member",
    perspective: UserGroupOrderingPerspective = "dashboard",
): ProfilePageSelectOption[] {
    return [
        {
            key: "joined",
            value: "joined",
            label: scope === "leader"
                ? perspective === "publicProfile"
                    ? localizer("user.page.groups.ordering.leading")
                    : localizer("profile.groups.ordering.assigned")
                : localizer("profile.groups.ordering.joined"),
            icon: UserPlus2,
        },
        { key: "new", value: "new", label: localizer("profile.groups.ordering.new"), icon: CalendarDays },
        { key: "members", value: "members", label: localizer("profile.groups.ordering.members"), icon: Users },
        { key: "discussions", value: "discussions", label: localizer("profile.groups.ordering.discussions"), icon: MessageSquareText },
        // ux-critique DASHBOARD-27. The ordering that uses the recency the card now shows. Without it
        // the chip is a signal a member can read and not act on, and "where should I go?" still costs a
        // group-by-group check. Offered on both scopes: a leader wants "which of my groups needs me"
        // for the same reason a member wants "which of mine is alive".
        { key: "active", value: "active", label: localizer("profile.groups.ordering.active"), icon: Flame },
    ];
}

export function normalizeUserSeriesOrdering(value?: string): UserSeriesOrderingStyle {
    switch ((value ?? "").trim().toLowerCase()) {
        case "followers":
            return "followers";
        case "discussions":
            return "discussions";
        default:
            return "new";
    }
}

export function getUserSeriesOrderingOptions(localizer: Localizer): ProfilePageSelectOption[] {
    return [
        { key: "new", value: "new", label: localizer("profile.series.ordering.new"), icon: CalendarDays },
        { key: "followers", value: "followers", label: localizer("profile.series.ordering.followers"), icon: Users },
        { key: "discussions", value: "discussions", label: localizer("profile.series.ordering.discussions"), icon: MessageSquareText },
    ];
}
